import _ from "lodash";
import ACLStore from "lib/aclStore";
import { getPostDetailFromFeedCache } from "Post/modules/PostGraphqlHelpers";
import { pypElementMapping } from "Constants/plannerElementMappings";
import { ATTACHMENT_OMIT_ATTRIBUTES } from "store/static";
import {
  APPROVED_EVIDENCE_STATUS,
  CURRICULUM_TYPE_DP,
  SUGGESTED_EVIDENCE_STATUS,
  DEFAULT_NAME_ORDER_PREFERENCE,
  NAME_MAPPING,
  CURRICULUM_TYPE_PYP,
  SUBJECT_BASED_GRADING,
  COURSE_BASED_GRADING,
} from "Constants/stringConstants";
import { hasModuleAdminAccess, MODULES } from "lib/staffPermissionUtils";
import {
  CAN_MANAGE_PORTFOLIO_POSTS_FOR_CLASSES,
  CAN_VIEW_PORTFOLIO_POSTS_FOR_CLASSES,
  CURRICULUM_LEVEL,
  ENABLE_GLOBAL_HEADER_ON_MULTIPLE_SCREENS,
  SHOW_COURSE_DISPLAY_NAME,
  STUDENT_NAME_ORDER,
  USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
} from "Constants/permissionConstants";
import { getCloudUrl } from "Constants";
import moment from "moment";

import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { getYoutubeVideoId } from "Utils";
import ACLStorage from "lib/aclService";
import {
  getIsConfigurationEnabledForCPAY,
  getSettingValue,
} from "modules/PermissionModule";

import {
  getAcademicSetupType,
  getIsLearningCoursesEnabled,
} from "Teacher/routes/LearningCourses/modules/Utils";
import { DATA_GRID_COLUMN_TYPES } from "StandardsTable/modules/constants";

export const ACTIVITY_VALUE_STATUS_INDIVIDUAL = "INDIVIDUAL";
export const ACTIVITY_VALUE_STATUS_ALL = "ALL";
export const TRIGGER_EVENT_TYPES = ["hover", "focus"];
/**
 * This function returns a sharing status string based on the sharing
 * permission given for the post.
 *
 * @param {*} param0
 * @param {Boolean} param0.isPrivate - Can any user see the post
 * @param {Boolean} param0.isHiddenFromStudents - Can any student see the post
 * @param {Boolean} param0.withPrefix - Show string with prefix "Shared with"
 *
 * @returns {String} - Returns the sharing status of a post
 */
export const getSharedWithString = ({
  isPrivate,
  isHiddenFromStudents,
  withPrefix = true,
  t,
}) => {
  if (!isPrivate && !isHiddenFromStudents) {
    return withPrefix
      ? t("journal:shared_with_students_families")
      : t("journal:students_n_families");
  } else if (isPrivate && !isHiddenFromStudents) {
    return withPrefix
      ? t("journal:shared_students")
      : t("journal:shared_with_sidebar_only_students");
  } else if (!isPrivate && isHiddenFromStudents) {
    return withPrefix
      ? t("journal:shared_families")
      : t("journal:only_families");
  } else if (isPrivate && isHiddenFromStudents) {
    return t("journal:post_visible_to_teachers_only");
  }
};

export const getSharedWithStringV2 = ({ isPrivate, isHiddenFromStudents }) => {
  if (!isPrivate && !isHiddenFromStudents) {
    return "everyone";
  } else if (isPrivate && !isHiddenFromStudents) {
    return "student";
  } else if (!isPrivate && isHiddenFromStudents) {
    return "family";
  } else if (isPrivate && isHiddenFromStudents) {
    return "private";
  }
};

export const isUserLiked = ({ likes, userId }) => {
  return _.find(
    _.get(likes, "edges", []),
    item => _.get(item, "node.user.id", "") == userId
  );
};

/**
 * This function checks whether a user has permission to see a journal post.
 *
 * @param {*} param0
 * @param {Object} param0.userInfo - Object has details of the logged in user
 * @param {Object} param0.postDetails - Object has journal post details
 *
 * @returns {Boolean} - Returns true if a user has correct permission to see the post
 */
export const checkPostDetailsVisibilityPermission = ({
  userInfo,
  postDetails,
}) => {
  const { isHiddenFromStudents, students } = postDetails;
  const { user_type: userType, id: userId } = userInfo;

  // Sharing permission
  if (userType == "student") {
    if (isHiddenFromStudents) {
      return false;
    }

    // else if (!isHiddenFromStudents) {
    //   // Check whether student is tagged in the post or not
    //   const taggedStudentList = _.get(students, "edges", []);
    //   const taggedStudent = _.find(
    //     taggedStudentList,
    //     student => student.node.id === userId
    //   );

    //   if (!taggedStudent) {
    //     return false;
    //   }
    // }
  }

  return true;
};

export const getCameraDeviceIdList = async () => {
  if ("mediaDevices" in navigator) {
    const devices = await navigator.mediaDevices.enumerateDevices();

    const videoDevices =
      _.filter(devices, device => device.kind === "videoinput") || [];
    return _.compact(_.map(videoDevices, x => _.get(x, "deviceId")));
  }
  return [];
};

export const checkIfVideoMimeTypeSupported = mimeType => {
  if (!window.MediaRecorder) return false;
  if (!MediaRecorder.isTypeSupported)
    return mimeType.startsWith("audio/mp4") || mimeType.startsWith("video/mp4");
  return MediaRecorder.isTypeSupported(mimeType);
};

/**It removes practice details except practice-id from taggedPractices */
export const getTransformedTaggedPractices = ({ taggedPractices }) => {
  return _.map(taggedPractices, ({ evaluationCycle, taggedPractice }) => ({
    evaluationCycle,
    taggedPractice: _.map(taggedPractice, ({ practice, ...rest }) => ({
      ...rest,
      practice: { id: practice.id },
    })),
  }));
};

export const getPracticesV2InputForPost = ({ taggedPractices }) => {
  const evidenceStatus = hasModuleAdminAccess({
    moduleName: MODULES.EVALUATION_CYCLES.id,
  })
    ? APPROVED_EVIDENCE_STATUS
    : SUGGESTED_EVIDENCE_STATUS;

  return _.first(
    _.map(
      taggedPractices,
      ({ evaluationCycle: { id = "" } = {}, taggedPractice }) => {
        return {
          evaluationCycleId: id,
          practices: _.map(taggedPractice, ({ practice: { id } }) => id),
          evidenceStatus,
        };
      }
    )
  );
};

const getPracticeWrappers = ({ taggedPractices }) => {
  const taggedPracticesForFirstEvaluationCycle = _.first(taggedPractices) ?? {};

  return taggedPracticesForFirstEvaluationCycle?.taggedPractice ?? [];
};

export const getAddedPracticesV2 = ({
  oldTaggedPractices,
  newTaggedPractices,
}) => {
  const oldPracticeWrappers = getPracticeWrappers({
    taggedPractices: oldTaggedPractices,
  });

  const newPracticeWrappers = getPracticeWrappers({
    taggedPractices: newTaggedPractices,
  });

  const addedPracticeWrappers = _.differenceWith(
    newPracticeWrappers,
    oldPracticeWrappers,
    (newPracticeWrapper, oldPracticeWrapper) => {
      const {
        practice: { id },
        evidenceStatus,
      } = newPracticeWrapper;
      const {
        practice: { id: oldId },
        evidenceStatus: oldEvidenceStatus,
      } = oldPracticeWrapper;

      return id === oldId && evidenceStatus === oldEvidenceStatus;
    }
  );

  return _.map(addedPracticeWrappers, ({ practice }) => practice);
};

export const getRemovedPracticesV2 = ({
  oldTaggedPractices,
  newTaggedPractices,
}) => {
  const oldPracticeWrappers = getPracticeWrappers({
    taggedPractices: oldTaggedPractices,
  });

  const newPracticeWrappers = getPracticeWrappers({
    taggedPractices: newTaggedPractices,
  });

  const removedPracticeWrappers = _.differenceWith(
    oldPracticeWrappers,
    newPracticeWrappers,
    (oldPracticeWrapper, newPracticeWrapper) => {
      const {
        practice: { id },
      } = oldPracticeWrapper;
      const {
        practice: { id: newId },
      } = newPracticeWrapper;
      return id === newId;
    }
  );

  return _.map(removedPracticeWrappers, ({ practice }) => practice);
};

export const isPostSeenByUser = ({ postId, userId }) => {
  const postDetails = getPostDetailFromFeedCache({ postId });
  return _.find(_.get(postDetails, "seen.edges", []), {
    node: { user: { id: userId } },
  });
};

export const hasTranslationPermission = () => {
  return ACLStore.can("Common:TranslateMessage");
};

// Function to get translation filter for journal post
export const _getPostTranslationFilter = ({
  userInfo,
  shouldTranslate,
  handleTranslation,
  localeLanguage,
}) => {
  // getSettingValue({
  //   name: "locale",
  //   organizationId: _.get(userInfo, "org_id", null),
  //   userId: _.get(userInfo, "id", null),
  //   userEntityType: _.get(userInfo, "userEntityType", null),
  // });

  const translationFilter = {
    targetedLanguage: localeLanguage,
    shouldTranslate: shouldTranslate,
    handleTranslation: handleTranslation,
  };
  return translationFilter;
};

export const getRatingsCollectively = ({
  currentRatings,
  newRating,
  students,
}) => {
  const updatedElementRatings = [...currentRatings];
  _.forEach(students, student => {
    const tempNewRating = { ...newRating };
    tempNewRating.studentId = _.get(student, "node.id", undefined);

    const currentIndex = _.findIndex(currentRatings, rating => {
      return (
        _.get(rating, "ibPYPElementType", undefined) ==
          _.get(tempNewRating, "ibPYPElementType", "") &&
        _.get(rating, "ibPYPElementId", "") ==
          _.get(tempNewRating, "ibPYPElementId", undefined) &&
        _.get(rating, "academicCriteriaSet.id", undefined) ==
          _.get(tempNewRating, "academicCriteriaSet.id", undefined) &&
        _.get(rating, "studentId", _.get(rating, "student.id", undefined)) ==
          _.get(student, "node.id", undefined)
      );
    });

    // Update exsiting rating
    if (currentIndex != -1) {
      tempNewRating.academicTerm = _.get(
        updatedElementRatings,
        [currentIndex, "academicTerm"],
        null
      );
      updatedElementRatings[currentIndex] = tempNewRating;
    } else updatedElementRatings.push(tempNewRating);
  });

  return updatedElementRatings;
};

export const compareRatingObjects = ({
  rating1,
  rating2,
  shouldCompareStudent = true,
  nodeTypeObjectVariable = 0,
  setId = -1,
  studentId = -1,
}) => {
  if (nodeTypeObjectVariable == 1) {
    rating1.ibPYPElementId = _.get(rating1, "id", "");
    rating1.ibPYPElementType = _.get(rating1, "ibPlannerElementType", "");
    rating1.studentId = studentId;
  }
  if (nodeTypeObjectVariable == 2) {
    rating2.ibPYPElementId = _.get(rating2, "id", "");
    rating2.ibPYPElementType = _.get(rating2, "ibPlannerElementType", "");
    rating2.studentId = studentId;
  }

  if (shouldCompareStudent) {
    return (
      // compare goal criteria (column)
      // compare specific goal (row)
      _.get(rating1, "studentId", _.get(rating1, "student.id", undefined)) ==
        _.get(rating2, "studentId", _.get(rating2, "student.id", undefined)) && // compare student
      _.get(rating1, "ibPYPElementType", undefined) ==
        _.get(rating2, "ibPYPElementType", "") && // compare learning goal category (table)
      _.get(rating1, "ibPYPElementId", "") ==
        _.get(rating2, "ibPYPElementId", undefined) &&
      _.get(rating1, "academicCriteriaSet.id", setId) ==
        _.get(rating2, "academicCriteriaSet.id", setId)
    );
  } else {
    return (
      // compare goal criteria (column)
      // compare specific goal (row)
      _.get(rating1, "ibPYPElementType", undefined) ==
        _.get(rating2, "ibPYPElementType", "") && // compare learning goal category (table)
      _.get(rating1, "ibPYPElementId", "") ==
        _.get(rating2, "ibPYPElementId", undefined) &&
      _.get(rating1, "academicCriteriaSet.id", setId) ==
        _.get(rating2, "academicCriteriaSet.id", setId)
    );
  }
};

export const getNestedTaggedLearningGoalsIds = (elementPYP, values) => {
  const fieldUID = _.get(
    pypElementMapping,
    _.get(elementPYP, "fieldUID", ""),
    ""
  );
  const mappedElements = _.keyBy(
    _.get(
      elementPYP,
      `resolvedMinimalTree[${elementPYP?.fieldUID}]`,
      _.get(elementPYP, `resolvedMinimalTree.nodes`, [])
    ),
    object => `${fieldUID}_${_.get(object, "id", "")}`
  );

  let currentObject = {};
  const learningGoalsIds = [];
  _.forEach(values, value => {
    currentObject = _.get(mappedElements, `${fieldUID}_${value}`, {});
    while (
      !_.isNil(
        _.get(mappedElements, `${fieldUID}_${currentObject?.parent}`, undefined)
      )
    ) {
      learningGoalsIds.push(
        `${fieldUID}_${_.cloneDeep(currentObject)?.parent}`
      );
      currentObject = _.get(
        mappedElements,
        `${fieldUID}_${_.get(currentObject, "parent", undefined)}`
      );
    }
    if (
      !_.isEmpty(currentObject) &&
      _.isNil(
        _.get(mappedElements, `${fieldUID}_${currentObject?.parent}`, undefined)
      )
    )
      learningGoalsIds.push(
        `${fieldUID}_${_.get(currentObject, "id", undefined)}`
      );

    learningGoalsIds.push(`${fieldUID}_${value}`);
  });
  return _.uniq(learningGoalsIds);
};

// returns array of keys which are different between objects
export const getDifferenceBetweenObjects = (obj1, obj2) =>
  _.reduce(
    obj1,
    function (result, value, key) {
      return _.isEqual(value, _.get(obj2, key)) ? result : result.concat(key);
    },
    []
  );

export const hasCommonPropertyInStudentActivity = ({
  studentId,
  studentActivityData,
  checkStatus = ACTIVITY_VALUE_STATUS_ALL,
}) => {
  let hasCommonProperty =
    _.get(studentActivityData, [studentId, "description", "status"]) ===
      checkStatus ||
    _.get(studentActivityData, [studentId, "startTime", "status"]) ===
      checkStatus ||
    _.get(studentActivityData, [studentId, "endTime", "status"]) ===
      checkStatus;

  _.forEach(
    _.get(studentActivityData, [studentId, "attachments"]),
    attachment => {
      if (!_.isNil(attachment?.value)) {
        hasCommonProperty =
          hasCommonProperty || _.get(attachment, "status") === checkStatus;
      }
    }
  );
  _.forEach(_.get(studentActivityData, [studentId, "criteria"]), value => {
    hasCommonProperty =
      hasCommonProperty || _.get(value, "status") === checkStatus;
  });
  return hasCommonProperty;
};
/* This function will clear the data of property from "ALL", which is tracked individually for every student */
const clearCommonActivityData = ({
  studentIds,
  currentStudentActivities,
  key = [],
}) => {
  let isAnyStudentContainCommonProp = false;
  for (const studentId of studentIds) {
    if (_.isEmpty(key)) {
      isAnyStudentContainCommonProp =
        isAnyStudentContainCommonProp ||
        hasCommonPropertyInStudentActivity({
          studentId,
          studentActivityData: currentStudentActivities,
        });
      if (isAnyStudentContainCommonProp) return;
      continue;
    }
    const status = _.get(currentStudentActivities, [
      studentId,
      ...key,
      "status",
    ]);
    if (
      _.has(currentStudentActivities, [studentId, ...key, "status"]) &&
      status === ACTIVITY_VALUE_STATUS_ALL
    ) {
      isAnyStudentContainCommonProp = true;
      break;
    }
  }
  if (!isAnyStudentContainCommonProp)
    _.unset(currentStudentActivities, ["ALL", ...key]);
};

/* Used to update data for ALL tab of activity tracking
currentStudentActivities: current state values of all student activities
key: key to update. this could be array containing "criteria", "description", "attachment", etc. [FOR criteria key pass in this format: 'criteria.<criteriaSetId>` ]
value: Value which is going to be updated for given key
returns: updated studentActivities object and shouldShowOverrideModal to tell wether show modal or not
*/
export const updateAllActivities = ({
  studentIds,
  currentStudentActivities,
  key = [],
  value,
}) => {
  const updatedStudentActivities = _.cloneDeep(currentStudentActivities);
  let shouldShowOverrideModal = false;
  const updatedValue = {
    ...value,
    status: ACTIVITY_VALUE_STATUS_ALL,
  };
  _.setWith(updatedStudentActivities, ["ALL", ...key], updatedValue, Object);
  _.forEach(studentIds, studentId => {
    if (!shouldShowOverrideModal)
      shouldShowOverrideModal =
        _.get(updatedStudentActivities, [studentId, ...key, "status"]) ===
        ACTIVITY_VALUE_STATUS_INDIVIDUAL;
    _.setWith(
      updatedStudentActivities,
      [studentId, ...key],
      updatedValue,
      Object
    );
  });

  return { updatedStudentActivities, shouldShowOverrideModal };
};

/* Used to remove data for ALL tab of activity tracking
currentStudentActivities: current state values of all student activities
key: key to update. this could be array containing "criteria", "description", "attachment", etc. [FOR criteria key pass in this format: 'criteria.<criteriaSetId>` ]
value: Value which is going to be updated for given key
returns: updated studentActivities object and shouldShowOverrideModal to tell wether show modal or not
*/
export const removeAllActivities = ({
  studentIds,
  currentStudentActivities,
  key = [],
  value,
}) => {
  const updatedStudentActivities = _.cloneDeep(currentStudentActivities);
  _.unset(updatedStudentActivities, ["ALL", ...key]);
  _.forEach(studentIds, studentId => {
    const isCommonlyTracked =
      _.get(updatedStudentActivities, [studentId, ...key, "status"]) ===
      ACTIVITY_VALUE_STATUS_ALL;
    if (isCommonlyTracked)
      _.unset(updatedStudentActivities, [studentId, ...key]);
  });

  return updatedStudentActivities;
};

/* Used to update data for specific tab of activity tracking
currentStudentActivities: current state values of all student activities
key: key to update. this could be array containing "criteria", "description", "attachment", etc. [FOR criteria key pass in this format: 'criteria.<criteriaSetId>` ]
value: Value which is going to be updated for given key
returns: updated studentActivities object
*/
export const updateSingleActivity = ({
  studentIds,
  studentId,
  currentStudentActivities,
  key = [],
  value,
}) => {
  const updatedStudentActivities = _.cloneDeep(currentStudentActivities);
  // if user has set value same as common tab then mark its status as ALL rather than keeping it as individual.
  const updatedValue = {
    ...value,
    status: _.isEqual(
      _.get(updatedStudentActivities, ["ALL", ...key, "value"]),
      _.get(value, "value")
    )
      ? ACTIVITY_VALUE_STATUS_ALL
      : ACTIVITY_VALUE_STATUS_INDIVIDUAL,
  };
  _.setWith(
    updatedStudentActivities,
    [studentId, ...key],
    updatedValue,
    Object
  );

  clearCommonActivityData({
    studentIds,
    currentStudentActivities: updatedStudentActivities,
    key,
  });
  return updatedStudentActivities;
};

/* Used to remove data for specific tab of activity tracking
currentStudentActivities: current state values of all student activities
key: key to remove. this could be array containing "criteria", "description", "attachment", etc. [FOR criteria key pass in this format: 'criteria.<criteriaSetId>` ]
value: Value which is going to be updated for given key
returns: updated studentActivities object
*/
export const removeSingleActivity = ({
  studentIds,
  studentId,
  currentStudentActivities,
  key = [],
  value,
}) => {
  const updatedStudentActivities = _.cloneDeep(currentStudentActivities);
  _.unset(updatedStudentActivities, [studentId, ...key]);
  if (_.isEmpty(key)) {
    // student is untagged, so if that student is the only one to use common data, then clear that data from common.
    _.forEach(["description", "startTime", "endTime"], prop => {
      if (
        _.get(currentStudentActivities, [studentId, prop, "status"]) == "ALL"
      ) {
        const otherStudentContainsProp =
          _.size(
            _.filter(
              currentStudentActivities,
              (studentNode, key) =>
                key != "ALL" && _.get(studentNode, [prop, "status"]) == "ALL"
            )
          ) > 1;
        if (!otherStudentContainsProp)
          _.unset(updatedStudentActivities, ["ALL", prop]);
      }
    });

    _.forEach(
      _.get(currentStudentActivities, [studentId, "attachments"]),
      attachmentItem => {
        if (_.get(attachmentItem, "status") == "ALL") {
          const processAttachment = _.pick(_.get(attachmentItem, "value"), [
            "url",
          ]);
          const otherStudentContainsProp =
            _.size(
              _.filter(currentStudentActivities, (studentNode, key) => {
                if (key == "ALL") return false;
                const currentAttachments = _.get(
                  studentNode,
                  ["attachments"],
                  []
                );
                return (
                  _.find(currentAttachments, currentAttachment =>
                    _.isEqual(
                      _.pick(currentAttachment?.value, ["url"]),
                      processAttachment
                    )
                  ) != undefined
                );
              })
            ) > 1;
          if (!otherStudentContainsProp) {
            const updatedAttachments = _.filter(
              _.get(updatedStudentActivities, ["ALL", "attachments"], []),
              attachment =>
                !_.isEqual(
                  _.pick(attachment?.value, ["url"]),
                  processAttachment
                )
            );
            if (_.size(updatedAttachments) > 0)
              _.set(
                updatedStudentActivities,
                ["ALL", "attachments"],
                updatedAttachments,
                Object
              );
            else _.unset(updatedStudentActivities, ["ALL", "attachments"]);
          }
        }
      }
    );

    _.forEach(
      _.get(currentStudentActivities, [studentId, "criteria"]),
      (item, criteriaId) => {
        if (_.get(item, "status") == "ALL") {
          const otherStudentContainsProp =
            _.size(
              _.filter(currentStudentActivities, (studentNode, key) => {
                if (key == "ALL") return false;
                const currentRatings = _.get(studentNode, ["criteria"], {});

                return (
                  _.find(
                    currentRatings,
                    (value, id) =>
                      id == criteriaId &&
                      _.get(value, "status") == "ALL" &&
                      _.get(value, "value") == _.get(item, "value")
                  ) != undefined
                );
              })
            ) > 1;
          if (!otherStudentContainsProp)
            _.unset(updatedStudentActivities, ["ALL", "criteria", criteriaId]);
        }
      }
    );
    return updatedStudentActivities;
  }
  clearCommonActivityData({
    studentIds,
    currentStudentActivities: updatedStudentActivities,
    key,
  });
  return updatedStudentActivities;
};

// Utiliy functions to manage attachments in activity tracking

export const updateAllActivityAttachments = ({
  studentIds,
  currentStudentActivities,
  key = [],
  attachments,
}) => {
  const updatedStudentActivities = _.cloneDeep(currentStudentActivities);
  const updatedAttachments = _.map(attachments, attachment => ({
    value: attachment,
    status: ACTIVITY_VALUE_STATUS_ALL,
  }));
  const commonAttachments = _.get(
    updatedStudentActivities,
    ["ALL", ...key],
    []
  );
  _.setWith(
    updatedStudentActivities,
    ["ALL", ...key],
    [...commonAttachments, ...updatedAttachments],
    Object
  );

  _.forEach(studentIds, studentId => {
    const currentStudentAttachments = _.get(
      updatedStudentActivities,
      [studentId, ...key],
      []
    );
    _.setWith(
      updatedStudentActivities,
      [studentId, ...key],
      [...currentStudentAttachments, ...updatedAttachments],
      Object
    );
  });

  return updatedStudentActivities;
};

export const updateSingleActivityAttachments = ({
  studentId,
  currentStudentActivities,
  key = [],
  attachments,
}) => {
  const updatedStudentActivities = _.cloneDeep(currentStudentActivities);
  const updatedAttachments = _.map(attachments, attachment => ({
    value: attachment,
    status: ACTIVITY_VALUE_STATUS_INDIVIDUAL,
  }));
  const currentAttachments = _.get(
    updatedStudentActivities,
    [studentId, ...key],
    []
  );
  _.setWith(
    updatedStudentActivities,
    [studentId, ...key],
    [...currentAttachments, ...updatedAttachments],
    Object
  );

  return updatedStudentActivities;
};

export const removeAttachmentFromActivities = ({
  studentIds,
  currentStudentActivities,
  key = [],
  attachmentToRemove,
}) => {
  const updatedAttachmentToRemove = _.pick(attachmentToRemove, ["url"]);
  const updatedStudentActivities = _.cloneDeep(currentStudentActivities);
  const updatedAttachments = _.filter(
    _.get(updatedStudentActivities, ["ALL", ...key], []),
    attachment =>
      !_.isEqual(_.pick(attachment?.value, ["url"]), updatedAttachmentToRemove)
  );
  _.setWith(
    updatedStudentActivities,
    ["ALL", ...key],
    updatedAttachments,
    Object
  );
  _.forEach(studentIds, studentId => {
    const updatedAttachments = _.filter(
      _.get(updatedStudentActivities, [studentId, ...key], []),
      attachment =>
        !_.isEqual(
          _.pick(attachment?.value, ["url"]),
          updatedAttachmentToRemove
        )
    );
    _.setWith(
      updatedStudentActivities,
      [studentId, ...key],
      updatedAttachments,
      Object
    );
  });

  return updatedStudentActivities;
};

export const removeSingleAttachmentFromActivity = ({
  studentIds,
  studentId,
  currentStudentActivities,
  key = [],
  attachmentToRemove,
}) => {
  const updatedStudentActivities = _.cloneDeep(currentStudentActivities);
  const updatedAttachmentToRemove = _.pick(attachmentToRemove, ["url"]);
  let commonAttachmentPresent = false;
  _.forEach(studentIds, currentStudentId => {
    if (currentStudentId === studentId) {
      const updatedAttachments = _.filter(
        _.get(updatedStudentActivities, [currentStudentId, ...key], []),
        attachment =>
          !_.isEqual(
            _.pick(attachment?.value, ["url"]),
            updatedAttachmentToRemove
          )
      );
      _.setWith(
        updatedStudentActivities,
        [currentStudentId, ...key],
        updatedAttachments,
        Object
      );
    }

    if (!commonAttachmentPresent) {
      const commonAttachment = _.find(
        _.get(updatedStudentActivities, [currentStudentId, ...key], []),
        attachment =>
          _.isEqual(
            _.pick(attachment?.value, ["url"]),
            updatedAttachmentToRemove
          )
      );
      if (
        !_.isNil(commonAttachment) &&
        _.get(commonAttachment, "status") === ACTIVITY_VALUE_STATUS_ALL
      )
        commonAttachmentPresent = true;
    }
  });

  // If attachment is added from common tab, and current student is last student having this attachment then remove it from ALL
  if (!commonAttachmentPresent) {
    const updatedAttachments = _.filter(
      _.get(updatedStudentActivities, ["ALL", ...key], []),
      attachment =>
        !_.isEqual(
          _.pick(attachment?.value, ["url"]),
          updatedAttachmentToRemove
        )
    );
    _.setWith(
      updatedStudentActivities,
      ["ALL", ...key],
      updatedAttachments,
      Object
    );
  }
  return updatedStudentActivities;
};

// Format student activity data from post details into above format
export const getFormattedStudentActivityData = postDetails => {
  const studentActivities = {};
  let activityType = undefined;
  _.forEach(_.get(postDetails, "items", []), itemElement => {
    if (_.get(itemElement, "itemType") === "STUDENT_ACTIVITY") {
      if (_.isNil(activityType))
        activityType = _.get(itemElement, "item.activityType");
      _.setWith(
        studentActivities,
        _.get(itemElement, ["item", "activityStudent", "id"]),
        {
          ..._.get(itemElement, "item", {}),
        },
        Object
      );
    }
  });

  const studentActivityData = {
    activityType: activityType,
  };

  _.forEach(_.get(postDetails, "students.edges", []), studentNode => {
    const studentId = _.get(studentNode, "node.id");

    if (!_.isNil(studentId)) {
      const currentStudentActivity = _.get(studentActivities, studentId);

      // Set properties which are directly available in Student Activity
      _.forEach(["description", "startTime", "endTime"], directProp => {
        const status = _.get(
          currentStudentActivity,
          ["metadata", "propertyStatus", directProp],
          "ALL"
        );

        if (!_.isNil(_.get(currentStudentActivity, directProp))) {
          if (status === "ALL")
            _.setWith(
              studentActivityData,
              ["ALL", directProp],
              {
                value: _.get(currentStudentActivity, directProp),
                status: status,
              },
              Object
            );

          _.setWith(
            studentActivityData,
            [studentId, directProp],
            {
              value: _.get(currentStudentActivity, directProp),
              status: status,
            },
            Object
          );
        }
      });

      // Set attachment data with status
      const activityAttachments = [];
      const commonAttachments = _.get(
        studentActivityData,
        ["ALL", "attachments"],
        []
      );
      _.forEach(
        _.get(currentStudentActivity, "attachmentGroups", []),
        attachmentGroup => {
          const attachment = _.first(_.get(attachmentGroup, "attachments", []));
          const status = _.get(
            attachmentGroup,
            "metadata.activityStatus",
            "ALL"
          );
          if (
            status === "ALL" &&
            _.isNil(
              // should not duplicate the common attachment in common tab
              _.find(commonAttachments, commonAttachment =>
                _.isEqual(
                  _.pick(commonAttachment?.value, ["url"]),
                  _.pick(attachment, ["url"])
                )
              )
            )
          )
            commonAttachments.push({
              attachmentGroupId: _.get(attachmentGroup, "id"),
              value: attachment,
              status: status,
              displaySequence: _.get(attachmentGroup, "displaySequence"),
            });
          activityAttachments.push({
            attachmentGroupId: _.get(attachmentGroup, "id"),
            value: attachment,
            status: status,
            displaySequence: _.get(attachmentGroup, "displaySequence"),
          });
        }
      );
      if (_.size(activityAttachments) > 0)
        _.setWith(
          studentActivityData,
          [studentId, "attachments"],
          activityAttachments,
          Object
        );

      if (_.size(commonAttachments) > 0)
        _.setWith(
          studentActivityData,
          ["ALL", "attachments"],
          commonAttachments,
          Object
        );

      // set criteria values
      const criteriaRatings = {};
      const commonCriteriaRatings = _.get(
        studentActivityData,
        ["ALL", "criteria"],
        {}
      );
      _.forEach(
        _.get(currentStudentActivity, "ibPYPElementRatings", []),
        rating => {
          const status = _.get(rating, "metadata.activityStatus", "ALL");
          if (!_.isNil(_.get(rating, "academicCriteriaValue.id"))) {
            if (status === "ALL")
              _.setWith(
                commonCriteriaRatings,
                _.get(rating, "academicCriteriaSet.id"),
                {
                  id: _.get(rating, "id"),
                  value: _.get(rating, "academicCriteriaValue.id"),
                  status: status,
                },
                Object
              );

            _.setWith(
              criteriaRatings,
              _.get(rating, "academicCriteriaSet.id"),
              {
                id: _.get(rating, "id"),
                value: _.get(rating, "academicCriteriaValue.id"),
                status: status,
              },
              Object
            );
          }
        }
      );
      if (_.size(commonCriteriaRatings) > 0)
        _.setWith(
          studentActivityData,
          ["ALL", "criteria"],
          commonCriteriaRatings,
          Object
        );

      if (_.size(criteriaRatings) > 0)
        _.setWith(
          studentActivityData,
          [studentId, "criteria"],
          criteriaRatings,
          Object
        );
      _.setWith(
        studentActivityData,
        [studentId, "activityId"],
        _.get(currentStudentActivity, "id"),
        Object
      );
      _.setWith(
        studentActivityData,
        [studentId, "individuallyTracked"],
        _.get(currentStudentActivity, "individuallyTracked"),
        Object
      );
    }
  });

  return studentActivityData;
};

// Get post activity input for creation
export const getStudentActivityInput = ({
  postDetails,
  taggedStudents = null,
  userId,
}) => {
  const { students = {}, studentActivityData = {} } = postDetails;

  const input = [];
  _.forEach(
    _.isNil(taggedStudents) ? _.get(students, "edges", []) : taggedStudents,
    student => {
      const studentId = _.get(student, "node.id");
      let individuallyTracked =
        _.get(studentActivityData, [studentId, "description", "status"]) ===
          ACTIVITY_VALUE_STATUS_INDIVIDUAL ||
        _.get(studentActivityData, [studentId, "startTime", "status"]) ===
          ACTIVITY_VALUE_STATUS_INDIVIDUAL ||
        _.get(studentActivityData, [studentId, "endTime", "status"]) ===
          ACTIVITY_VALUE_STATUS_INDIVIDUAL;

      if (!_.isNil(studentId)) {
        const studentActivity = {
          studentId: studentId,
          activityType: _.get(studentActivityData, "activityType"),
          description: _.get(studentActivityData, [
            studentId,
            "description",
            "value",
          ]),
          startTime: _.get(studentActivityData, [
            studentId,
            "startTime",
            "value",
          ]),
          endTime: _.get(studentActivityData, [studentId, "endTime", "value"]),
          attachmentGroups: _.compact(
            _.map(
              _.get(studentActivityData, [studentId, "attachments"]),
              (attachment, index) => {
                if (!_.isNil(attachment?.value)) {
                  individuallyTracked =
                    individuallyTracked ||
                    _.get(attachment, "status", "ALL") ===
                      ACTIVITY_VALUE_STATUS_INDIVIDUAL;
                  return {
                    parentType: "STUDENT_ACTIVITY",
                    attachments: [
                      _.omit(attachment?.value, [
                        ...ATTACHMENT_OMIT_ATTRIBUTES,
                        "id",
                      ]),
                    ],
                    metadata: {
                      activityStatus: _.get(attachment, "status", "ALL"),
                    },
                    displaySequence: index + 1,
                  };
                }
                return null;
              }
            )
          ),
          ibPYPElementRatings: _.map(
            _.get(studentActivityData, [studentId, "criteria"], {}),
            (value, criteriaSetId) => {
              individuallyTracked =
                individuallyTracked ||
                _.get(value, "status", "ALL") ===
                  ACTIVITY_VALUE_STATUS_INDIVIDUAL;
              return {
                studentId: studentId,
                parentType: "STUDENT_ACTIVITY",
                academicCriteriaSetId: criteriaSetId,
                academicCriteriaValueId: _.get(value, "value"),
                createdBy: userId,
                ibPYPElementType: "STUDENT_ACTIVITY",
                metadata: { activityStatus: _.get(value, "status", "ALL") },
              };
            }
          ),
          individuallyTracked: individuallyTracked,
          metadata: {
            propertyStatus: {
              description: _.get(studentActivityData, [
                studentId,
                "description",
                "status",
              ]),
              startTime: _.get(studentActivityData, [
                studentId,
                "startTime",
                "status",
              ]),
              endTime: _.get(studentActivityData, [
                studentId,
                "endTime",
                "status",
              ]),
            },
          },
        };
        if (
          !_.isEmpty(_.get(studentActivity, "ibPYPElementRatings", [])) ||
          !_.isEmpty(_.get(studentActivity, "startTime", "")) ||
          !_.isEmpty(_.get(studentActivity, "endTime", [])) ||
          !_.isEmpty(_.get(studentActivity, "description", "")) ||
          !_.isEmpty(_.get(studentActivity, "attachmentGroups", []))
        )
          input.push(studentActivity);
      }
    }
  );
  return input;
};

// Get post activities details which are modified during edit post
export const getModifiedStudentActivitiesInput = ({
  studentActivityData,
  originalStudentActivityData,
  taggedStudentsIds,
  userId,
}) => {
  const modifiedStudentIds = getDifferenceBetweenObjects(
    originalStudentActivityData,
    studentActivityData
  );

  const input = [];
  _.forEach(
    _.intersection(taggedStudentsIds, modifiedStudentIds),
    modifiedStudentId => {
      // Add all current activity data ratings (ratings will be updated on conflict)
      const newAddedRatings = [];
      _.forEach(
        _.get(studentActivityData, [modifiedStudentId, "criteria"]),
        (value, criteriaSetId) => {
          if (
            !_.isEqual(
              _.get(originalStudentActivityData, [
                modifiedStudentId,
                "criteria",
                criteriaSetId,
                "value",
              ]),
              _.get(studentActivityData, [
                modifiedStudentId,
                "criteria",
                criteriaSetId,
                "value",
              ])
            )
          )
            newAddedRatings.push({
              studentId: modifiedStudentId,
              parentId: _.get(originalStudentActivityData, [
                modifiedStudentId,
                "activityId",
              ]),
              parentType: "STUDENT_ACTIVITY",
              academicCriteriaSetId: criteriaSetId,
              academicCriteriaValueId: _.get(value, "value"),
              createdBy: userId,
              ibPYPElementType: "STUDENT_ACTIVITY",
              metadata: { activityStatus: _.get(value, "status", "ALL") },
            });
        }
      );

      // If rating is present in DB and not present in redux, then it is removed
      const removedRatings = [];
      _.forEach(
        _.get(originalStudentActivityData, [modifiedStudentId, "criteria"]),
        (value, criteriaSetId) => {
          if (
            _.isNil(
              _.get(studentActivityData, [
                modifiedStudentId,
                "criteria",
                criteriaSetId,
                "value",
              ])
            )
          ) {
            removedRatings.push(_.get(value, "id"));
          }
        }
      );

      // Get removed attachments
      const removedAttachmentGroupIds = [];
      _.forEach(
        _.get(originalStudentActivityData, [modifiedStudentId, "attachments"]),
        attachmentElement => {
          const existingAttachment = _.find(
            _.get(studentActivityData, [modifiedStudentId, "attachments"]),
            tempAttachment =>
              _.isEqual(
                _.pick(tempAttachment?.value, ["url"]),
                _.pick(attachmentElement?.value, ["url"])
              )
          );
          if (_.isNil(existingAttachment))
            removedAttachmentGroupIds.push(
              _.get(attachmentElement, "attachmentGroupId")
            );
        }
      );

      // Get newly added attachments
      const newAttachmentGroups = [];
      const updatedAtttachmentGroups = [];
      _.forEach(
        _.get(studentActivityData, [modifiedStudentId, "attachments"]),
        (attachmentElement, index) => {
          if (!_.isNil(attachmentElement?.value)) {
            const attachmentItemUrl = _.get(attachmentElement?.value, "url");

            const isAttachmentPresent = _.find(
              _.get(originalStudentActivityData, [
                modifiedStudentId,
                "attachments",
              ]),
              attachmentItr => {
                if (_.isNil(attachmentItemUrl)) {
                  return false;
                }
                return _.get(attachmentItr, "value.url") === attachmentItemUrl;
              }
            );
            if (isAttachmentPresent) {
              updatedAtttachmentGroups.push({
                id: _.get(isAttachmentPresent, "attachmentGroupId"),
                displaySequence: index + 1,
              });
            } else {
              newAttachmentGroups.push({
                parentType: "STUDENT_ACTIVITY",
                attachments: _.omit(attachmentElement?.value, [
                  ...ATTACHMENT_OMIT_ATTRIBUTES,
                  "id",
                ]),
                displaySequence: index + 1,
                metadata: {
                  activityStatus: _.get(attachmentElement, "status", "ALL"),
                },
              });
            }
          }
        }
      );

      const metadata = {
        propertyStatus: {
          description: _.get(studentActivityData, [
            modifiedStudentId,
            "description",
            "status",
          ]),
          startTime: _.get(studentActivityData, [
            modifiedStudentId,
            "startTime",
            "status",
          ]),
          endTime: _.get(studentActivityData, [
            modifiedStudentId,
            "endTime",
            "status",
          ]),
        },
      };

      const studentInput = {
        id: _.get(originalStudentActivityData, [
          modifiedStudentId,
          "activityId",
        ]),
        description: _.get(studentActivityData, [
          modifiedStudentId,
          "description",
          "value",
        ]),
        startTime: _.get(studentActivityData, [
          modifiedStudentId,
          "startTime",
          "value",
        ]),
        endTime: _.get(studentActivityData, [
          modifiedStudentId,
          "endTime",
          "value",
        ]),
        metadata: metadata,
        newAddedRatings: newAddedRatings,
        removedRatings: removedRatings,
        removeAttachmentGroupIds: removedAttachmentGroupIds,
        individuallytracked: hasCommonPropertyInStudentActivity({
          studentId: modifiedStudentId,
          studentActivityData,
        }),
        attachmentGroups: newAttachmentGroups,
        updateAttachmentGroups: updatedAtttachmentGroups,
      };

      input.push(studentInput);
    }
  );
  return input;
};

// Get Ids of Student activities which are completely removed during edit post
export const getIdsOfRemovedStudentActivities = ({
  removedStudentIds,
  originalStudentActivityData,
}) => {
  const input = [];
  _.forEach(removedStudentIds, studentId => {
    if (!_.isNil(_.get(originalStudentActivityData, studentId))) {
      input.push(_.get(originalStudentActivityData, [studentId, "activityId"]));
    }
  });
  return _.compact(input);
};

const isEqualWithoutOrder = (array1, array2) =>
  _.size(_.xor(array1, array2)) === 0;

export const isStudentDataEqual = (student1, student2) => {
  let isDescriptionEqual = false;
  const description1 = _.get(student1, "description.value", "");
  const description2 = _.get(student2, "description.value", "");
  if (_.isEqual(description1, description2)) isDescriptionEqual = true;

  const isStartTimePresent =
    !_.isNil(_.get(student1, "startTime.value")) ||
    !_.isNil(_.get(student2, "startTime.value"));
  let isStartTimeEqual = false;
  const startTime1 = _.get(student1, "startTime.value", "");
  const startTime2 = _.get(student2, "startTime.value", "");
  if (_.isEqual(startTime1, startTime2)) isStartTimeEqual = true;

  const isEndTimePresent =
    !_.isNil(_.get(student1, "endTime.value")) ||
    !_.isNil(_.get(student2, "endTime.value"));
  let isEndTimeEqual = false;
  const endTime1 = _.get(student1, "endTime.value", "");
  const endTime2 = _.get(student2, "endTime.value", "");
  if (_.isEqual(endTime1, endTime2)) isEndTimeEqual = true;

  let areCriteriasEqual = false;
  const criteria1 = _.get(student1, "criteria", {});
  const criteria2 = _.get(student2, "criteria", {});
  if (_.isEqual(_.keys(criteria1), _.keys(criteria2))) {
    const criteriaValue1 = _.reduce(
      criteria1,
      (result, value) => {
        result.push(_.get(value, "value", ""));
        return result;
      },
      []
    );

    const criteriaValue2 = _.reduce(
      criteria2,
      (result, value) => {
        result.push(_.get(value, "value", ""));
        return result;
      },
      []
    );

    if (isEqualWithoutOrder(criteriaValue1, criteriaValue2))
      areCriteriasEqual = true;
  }

  let areAttachmentsEqual = false;
  const attachmentUrls1 = _.reduce(
    _.get(student1, "attachments", []),
    (result, value, key) => {
      result.push(_.get(value, "value.url", ""));
      return result;
    },
    []
  );
  const attachmentsUrl2 = _.reduce(
    _.get(student2, "attachments", []),
    (result, value, key) => {
      result.push(_.get(value, "value.url", ""));
      return result;
    },
    []
  );
  if (isEqualWithoutOrder(attachmentUrls1, attachmentsUrl2))
    areAttachmentsEqual = true;

  if (!isStartTimePresent && !isEndTimePresent)
    return isDescriptionEqual && areCriteriasEqual && areAttachmentsEqual;

  if (!isStartTimePresent && isEndTimePresent)
    return (
      isDescriptionEqual &&
      isEndTimeEqual &&
      areCriteriasEqual &&
      areAttachmentsEqual
    );

  if (isStartTimePresent && !isEndTimePresent)
    return (
      isDescriptionEqual &&
      isStartTimeEqual &&
      areCriteriasEqual &&
      areAttachmentsEqual
    );

  return (
    isDescriptionEqual &&
    isStartTimeEqual &&
    isEndTimeEqual &&
    areCriteriasEqual &&
    areAttachmentsEqual
  );
};

export const getLocaleForOverrideModal = ({
  property,
  operation,
  studentsCount,
  t,
}) => {
  let localeString = t("common:action_msg", { action: "common:update" }); // default case
  let notifyLocale = t("toastMsgs:post_updated_successfully");
  switch (operation) {
    case "ADD":
      localeString = t("journal:activity_adding_property", {
        property,
        students: studentsCount,
      });
      notifyLocale = t("journal:activity_adding_property", {
        property,
        students: studentsCount,
      });
      break;
    case "DELETE":
      localeString = t("journal:activity_deleting_property", {
        property,
        students: studentsCount,
      });
      notifyLocale = t("journal:activity_deleted_property", {
        property,
        students: studentsCount,
      });
      break;
    case "UPDATE":
      localeString = t("journal:activity_updating_property", {
        property,
        students: studentsCount,
      });
      notifyLocale = t("journal:activity_updated_property", {
        property,
        students: studentsCount,
      });
      break;
  }
  return { localeString, notifyLocale };
};

// configurations - set of settings that are affected
// configInputProps - contains courseId, portal name etc.
export const checkAndUpdateForPostApprovalSetting = ({
  configurations,
  configInputProps,
  updateSettings,
}) => {
  const configuration = _.get(configurations, "configuration");
  const newValue = _.get(configurations, "newValue");
  const courseId = _.get(configInputProps, "courseId");
  if (
    _.get(configuration, "configuration") ==
      "EnablePostApprovalRequiredForNewStudents" &&
    !_.isNil(courseId) &&
    newValue == false
  ) {
    updateSettings({
      settings: [
        {
          name: "TeacherPortal:ClosedPostApprovalRequestModal",
          value: JSON.stringify([]),
        },
      ],
      queryVariablesSrc: CURRICULUM_LEVEL,
      mutationVariablesSrc: "UPDATE_COURSE_ONLY_SETTINGS",
      showToastMsg: false,
      courseId,
    });
  }
};
export const getIconUrl = url => {
  const reg = new RegExp("^(https?://)?([^/]+)");
  return `${getCloudUrl()}${url.replace(reg, "")}`;
};

const _getFilteredToolResponsesByUserType = ({
  toolResponses = [],
  isSharedWithStudent = false,
  userType,
}) => {
  if (userType == "staff" || isSharedWithStudent) {
    return toolResponses;
  }
  return _.map(toolResponses, toolResponse => {
    const cloneResponse = _.cloneDeep(toolResponse);
    cloneResponse.toolResponses = _.filter(
      _.get(cloneResponse, "toolResponses", []),
      toolResponse => toolResponse.creatorType === "STUDENT"
    );
    return cloneResponse;
  });
};

export const getFilteredToolResponsesByUserTypeMemoized = _.memoize(
  _getFilteredToolResponsesByUserType,
  ({ toolResponses, isSharedWithStudent, userType }) => {
    const cacheData = _.map(toolResponses, toolResponse => {
      const sortedToolResponses = _.sortBy(
        _.get(toolResponse, "toolResponses", []),
        tool => _.get(tool, "id", "")
      );
      const toolResponsesSummary = _.map(sortedToolResponses, response =>
        _.cloneDeep(response)
      );
      return {
        id: _.get(toolResponse, "id"),
        toolId: _.get(toolResponse, "toolId"),
        toolType: _.get(toolResponse, "toolType"),
        toolResponses: toolResponsesSummary,
        responseCount: _.size(toolResponsesSummary),
      };
    });

    return JSON.stringify({
      userType,
      isSharedWithStudent,
      toolData: cacheData,
    });
  }
);

export const getCombinedDateAndTimeMoment = dateTimeObject => {
  if (
    !_.isEmpty(dateTimeObject) &&
    !_.isEmpty(dateTimeObject?.date) &&
    !_.isEmpty(dateTimeObject?.time)
  ) {
    return moment(
      dateTimeObject?.date?.format("YYYY-MM-DD") +
        " " +
        dateTimeObject?.time?.format("HH:mm")
    );
  }
};
export const isDPSubjectJournalEnabled = curriculumType => {
  return (
    curriculumType === CURRICULUM_TYPE_DP &&
    (canViewPortfolioPosts() ||
      ACLStore.can("Common:Portfolio") ||
      ACLStore.can("Common:PortfolioFamily"))
  );
};

export const showActivityTracking = () => {
  return ACLStore.can("EnableActivityTracker");
};

export const isPortFolioManagerEnabled = () => {
  return ACLStore.can("AdminPortal:PortfolioManager");
};

export const enableTermRestriction = () => {
  return ACLStore.can("FeatureFlag:EnableTermRestriction");
};

export const isPortfolioFilterRevamp = () => {
  return (
    isPortfolioParityEnabled() &&
    ACLStore.can("FeatureFlag:PortfolioFilterRevamp")
  );
};

export const isPortfolioEnabled = () => {
  return ACLStore.can("Common:Portfolio") || canViewPortfolioPosts();
};

export const getPostTranslationFilterMemoized = _.memoize(
  _getPostTranslationFilter,
  params => {
    const {
      userInfo,
      handleTranslation,
      localeLanguage,
      shouldTranslate,
    } = params;
    return JSON.stringify({
      userInfo: userInfo.id,
      handleTranslation,
      localeLanguage,
      shouldTranslate,
    });
  }
);

export const canManagePortfolioPosts = () => {
  return ACLStorage.getPermissionValue(CAN_MANAGE_PORTFOLIO_POSTS_FOR_CLASSES);
};

export const canViewPortfolioPosts = () => {
  return ACLStorage.getPermissionValue(CAN_VIEW_PORTFOLIO_POSTS_FOR_CLASSES);
};

export const canManagePortfolioActions = userType => {
  if (userType === "staff") {
    return (
      canManagePortfolioPosts() ||
      isPortfolioReviewSetupEnabledAndIsReviewRoute()
    );
  }
  return true;
};

export const getPostTranslationFilter = ({
  userInfo,
  shouldTranslate,
  handleTranslation,
}) => {
  const localeLanguage = _.get(localStorage, "i18nextLng", "en");
  return getPostTranslationFilterMemoized({
    userInfo,
    shouldTranslate,
    handleTranslation,
    localeLanguage,
  });
};

export const fetchStudentActivitiesMemoized = _.memoize(
  ({ items }) => {
    if (_.isEmpty(items)) return items;

    return _.filter(items, _.matchesProperty("itemType", "STUDENT_ACTIVITY"));
  },
  ({ items }) => {
    // Create cache key based on data that actually changes
    const cacheData = _.map(items, item => {
      const activityItem = _.get(item, "item");
      return {
        id: _.get(activityItem, "id"),
        itemType: _.get(item, "itemType"),
        ratingIds: _.map(
          _.get(activityItem, "ibPYPElementRatings", []),
          rating => _.get(rating, "id")
        ),
        criteriaValueIds: _.map(
          _.get(activityItem, "ibPYPElementRatings", []),
          rating => _.get(rating, "academicCriteriaValue.id")
        ),
        commentIds: _.map(
          _.get(activityItem, "comments.edges", []),
          edge =>
            `${_.get(edge, "node.id", "")}${_.get(
              edge,
              "node.label",
              ""
            )}${_.get(edge, "node.isCommentRead", "")}`
        ).join("|"),
        likeIds: _.map(_.get(activityItem, "likes.edges", []), edge =>
          _.get(edge, "id", "")
        ),
        unreadCountV2: _.get(activityItem, "comments.unreadCountV2", 0),
      };
    });
    return JSON.stringify(cacheData);
  }
);

function collectAllIdentifiers(items) {
  const acc = [];
  const stack = [..._.toArray(items)]; // ensure array
  while (stack.length) {
    const current = stack.pop();

    if (_.isArray(current)) {
      stack.push(...current);
    } else if (_.isObject(current) && current !== null) {
      for (const [key, value] of Object.entries(current)) {
        if (
          key === "id" &&
          (typeof value === "string" || typeof value === "number")
        ) {
          acc.push(value);
        } else {
          stack.push(value);
        }
      }
    }
  }

  return acc;
}

export const fetchStudentRoutinesMemoized = _.memoize(
  ({ items }) => {
    if (!_.size(items)) return items;

    return _.filter(
      items,
      item => item.itemType === "STUDENT_TEMPLATE_RESPONSE"
    );
  },
  ({ items }) => {
    if (!_.size(items)) return "empty";

    const identifiers = collectAllIdentifiers(items);

    const itemTypes = _.map(items, item => item.itemType || "");

    const commentContext = [];

    _.forEach(items, item => {
      const edges = _.get(item, "item.comments.edges", []);
      _.forEach(edges, edge => {
        commentContext.push({
          id: _.get(edge, "node.id", ""),
          label: _.get(edge, "node.label", ""),
        });
      });
    });

    return JSON.stringify({
      identifiers: _.toArray(identifiers),
      itemTypes: _.toArray(itemTypes),
      commentContext,
    });
  }
);

export const fetchfilteredStudentActivities = _.memoize(
  (studentActivities, userType, studentId) => {
    if (_.isEmpty(studentActivities)) return studentActivities;

    if (userType !== "staff" && !_.isNil(studentId)) {
      return _.filter(
        studentActivities,
        activityData =>
          _.get(activityData, "item.activityStudent.id") === studentId
      );
    }

    return studentActivities;
  },
  (studentActivities, userType, studentId) => {
    const cacheData = _.map(studentActivities, activity => {
      const activityItem = _.get(activity, "item");
      return {
        id: _.get(activityItem, "id"),
        criteriaValueIds: _.map(
          _.get(activityItem, "ibPYPElementRatings", []),
          rating => _.get(rating, "academicCriteriaValue.id")
        ),
        commentIds: _.map(
          _.get(activityItem, "comments.edges", []),
          edge =>
            `${_.get(edge, "node.id", "")}${_.get(
              edge,
              "node.label",
              ""
            )}${_.get(edge, "node.isCommentRead", "")}`
        ).join("|"),
        likeIds: _.map(_.get(activityItem, "likes.edges", []), edge =>
          _.get(edge, "id", "")
        ),
        unreadCountV2: _.get(activityItem, "comments.unreadCountV2", 0),
      };
    });

    return JSON.stringify({
      userType,
      studentId,
      activities: cacheData,
    });
  }
);

export const fetchfilteredStudentRoutines = _.memoize(
  (studentActivities, userType, studentId) => {
    if (!_.size(studentActivities)) return studentActivities;

    if (userType !== "staff" && !_.isNil(studentId)) {
      return _.filter(
        studentActivities,
        activityData =>
          _.get(activityData, "item.routineStudent.id", "") === studentId
      );
    }

    return studentActivities;
  },
  (studentActivities, userType, studentId) => {
    if (!_.size(studentActivities)) {
      return `empty|${userType}|${studentId}`;
    }

    const identifiers = collectAllIdentifiers(studentActivities);

    const itemTypes = _.map(studentActivities, item =>
      _.get(item, "itemType", "")
    );

    const commentKeys = [];

    _.forEach(studentActivities, activity => {
      const edges = _.get(activity, "item.comments.edges", []);
      _.forEach(edges, edge => {
        const id = _.get(edge, "node.id", "");
        const label = _.get(edge, "node.label", "");
        commentKeys.push(`${id}-${label}`);
      });
    });

    const cacheKey = [
      userType,
      studentId,
      ...identifiers,
      ...itemTypes,
      ...commentKeys,
    ].join("|");

    return cacheKey;
  }
);

export const isPortfolioParityEnabled = () => {
  return (
    ACLStore.can("FeatureFlag:PortfolioParity") ||
    _.includes(window.location.href, "/portfolio-review")
  );
};

export const isExtendedAudioRecordingEnabled = () => {
  return ACLStore.can("FeatureFlag:EnableExtendedAudiorecording");
};

export const isPortfolioAIEnabled = () => {
  return ACLStore.can("FeatureFlag:PortfolioAIWeb");
};

export const isPortfolioAIEnabledAndIsJournalRoute = (userType = "staff") => {
  return (
    userType === "staff" &&
    _.includes(window.location.href, "/journal") &&
    isPortfolioAIEnabled()
  );
};

export const isEvidencingEditRoute = () => {
  return _.includes(window.location.href, "/edit/evidence");
};

export const isEvidencingRoute = () => {
  return _.includes(window.location.href, "/evidence");
};

export const replaceCaptionWithIdAndNameMapping = (text, mapping) => {
  const regex = /⌴st-(\d+)⌴/g;
  const replacedText = _.replace(text, regex, (match, captureGroup) => {
    const replacement = mapping[captureGroup];
    return replacement ?? match;
  });

  return replacedText;
};

export const cleanNoteMessage = (title = "", userType = "staff") => {
  if (isPortfolioAIEnabledAndIsJournalRoute(userType)) {
    return (
      title
        // unwrap highlighted spans
        ?.replace(
          /<span style="background-color: rgba\(106, 177, 255, 0.4\);[\s]*padding-top: 3px;">([\s\S]*?)<\/span>/gi,
          "$1"
        )
        // remove <div> wrappers
        .replace(/<\/?div[^>]*>/gi, "")
        // convert <br> to newlines
        .replace(/<br\s*\/?>/gi, "\n")
        // remove <font ...> tags but keep inner text
        .replace(/<\/?font[^>]*>/gi, "")
        .replace(/&gt;/g, ">")
        .replace(/&lt;/g, "<")
    );
  }
  return title;
};

export const isPortfolioParityEnabledAndIsJournalRoute = () => {
  return (
    _.includes(window.location.href, "/journal") ||
    _.includes(window.location.href, "/portfolio-review") ||
    _.includes(window.location.href, "/routines") ||
    _.includes(window.location.href, "/family-portfolio-summary") ||
    (_.includes(window.location.href, "/studentFeed") &&
      isPortfolioParityEnabled())
  );
};

export const isGlobalHeaderOnMultipleScreensEnabled = () => {
  return ACLStore.can(ENABLE_GLOBAL_HEADER_ON_MULTIPLE_SCREENS);
};

export const getFormattedUserNameWithoutPronoun = ({ user, type, ...rest }) => {
  if (_.get(user, "type") === "student" || _.isEqual(type, "student")) {
    let fullName = "";
    const defaultStudentNameOrderPreference =
      getSettingValue({
        name: STUDENT_NAME_ORDER,
        readFromUserAndOrgLevel: true,
      }) || DEFAULT_NAME_ORDER_PREFERENCE;

    if (_.includes(defaultStudentNameOrderPreference, "PRONOUNS")) {
      defaultStudentNameOrderPreference.forEach(order => {
        if (order !== "PRONOUNS") {
          const propertyName = NAME_MAPPING[order];
          const propertyValue = _.get(user, propertyName, "");

          if (propertyValue) {
            if (fullName) {
              fullName += " ";
            }
            fullName += propertyValue;
          }
        }
      });
      return fullName;
    }
    return getFormattedUserName({ user, type, ...rest });
  }
  return getFormattedUserName({ user, type, ...rest });
};

export const isPortfolioReviewSetupEnabled = () => {
  return ACLStore.can("EnablePortfolioReviewer");
};

export const isPortfolioReviewSetupEnabledAndIsReviewRoute = () => {
  return (
    _.includes(window.location.href, "/portfolio-review") &&
    isPortfolioReviewSetupEnabled()
  );
};

export const isPortfolioReviewSetupEnabledAndIsJournalRoute = () => {
  return (
    _.includes(window.location.href, "/journal") &&
    isPortfolioReviewSetupEnabled()
  );
};

export const isPortfolioRevampAndIsJournalRoute = () => {
  return (
    isPortfolioFilterRevamp() &&
    (_.includes(window.location.href, "/journal") ||
      _.includes(window.location.href, "/routines") ||
      _.includes(window.location.href, "/studentFeed") ||
      _.includes(window.location.href, "/family-portfolio-summary"))
  );
};

export const isPortfolioFilterRevampAndIsStudentFeedlRoute = () => {
  return (
    (_.includes(window.location.href, "/studentFeed") ||
      _.includes(window.location.href, "/family-portfolio-summary")) &&
    isPortfolioFilterRevamp()
  );
};

export const getPostDurationFilters = filters => {
  return {
    fromDate: !_.isNull(_.get(filters, "duration.fromDate"))
      ? moment(
          _.get(filters, "duration.fromDate")?.format("YYYY-MM-DD")
        ).toISOString()
      : null,
    toDate: !_.isNull(_.get(filters, "duration.toDate"))
      ? moment(_.get(filters, "duration.toDate")?.format("YYYY-MM-DD"))
          .add({ hours: 23, minutes: 59 })
          .toISOString()
      : null,
  };
};

export const onEnterOrSpaceKeyDownWithPreventDefault = (
  event,
  cb,
  preventDefault = true
) => {
  if (!event) return false;
  // If the keydown event is triggered with either of (Enter or Space) key is pressed,
  // then return true
  if (_.includes(["Enter", "Space"], event.code)) {
    // prevent default is applied because sometimes pressing space was causing the page to scroll down
    preventDefault && event.preventDefault();
    cb(event);
  }
};

export const getIsMultipleUnitTaggingEnabled = userType => {
  return (
    (userType === "staff" && ACLStore.can("EnableMultipleUnitTagging")) ||
    (userType === "staff" &&
      _.includes(window.location.href, "/portfolio-review"))
  );
};

export const isMultipleUnitTaggingEnabled = () => {
  return (
    ACLStore.can("EnableMultipleUnitTagging") ||
    _.includes(window.location.href, "/portfolio-review")
  );
};

export const getIsNewGoalTaggingEnabled = () => {
  return (
    ACLStore.can("FeatureFlag:EnableNewGoalTagging") ||
    _.includes(window.location.href, "/portfolio-review")
  );
};

export const getTaggedGoalsCount = resources => {
  return _.reduce(
    resources,
    (acc, element) => {
      const count = _.isArray(_.get(element, "value"))
        ? _.get(element, "value.length", 0)
        : 0;
      return acc + count;
    },
    0
  );
};

export const getMultipleUnitPlanFields = unitPlans => {
  const unitPlanFields = [];
  _.forEach(unitPlans, unit => {
    unitPlanFields.push(..._.get(unit, "fields"));
  });
  return unitPlanFields;
};

export const getMultipleUnitPlanSubjects = (unitPlans, isAssessment) => {
  const unitPlanFields = [];
  _.forEach(unitPlans, unit => {
    if (
      !_.isUndefined(
        isAssessment
          ? _.get(unit, "unitPlan.subjects.resolvedMinimalTree.subjects")
          : _.get(unit, "subjects.resolvedMinimalTree.subjects")
      )
    ) {
      unitPlanFields.push(
        ...(isAssessment
          ? _.get(unit, "unitPlan.subjects.resolvedMinimalTree.subjects")
          : _.get(unit, "subjects.resolvedMinimalTree.subjects"))
      );
    }
  });
  return unitPlanFields;
};

export const isUnitPlanOrAssessmentTagged = postTaggedEntities => {
  return _.some(
    postTaggedEntities,
    entity =>
      entity?.entityType === "UNIT_PLAN" || entity?.entityType === "ASSESSMENT"
  );
};

export const isUnitPlanTagged = postTaggedEntities => {
  return _.some(
    postTaggedEntities,
    entity => entity?.entityType === "UNIT_PLAN"
  );
};

export const isAssessmentTagged = postTaggedEntities => {
  return _.some(
    postTaggedEntities,
    entity => entity?.entityType === "ASSESSMENT"
  );
};

export const getUnitPlanFields = postTaggedEntities => {
  const fields = [];
  _.forEach(postTaggedEntities, entity => {
    if (entity?.entityType === "UNIT_PLAN") {
      fields.push(..._.get(entity, "entity.fields", []));
    }
  });
  return fields;
};

export const getTaggedAssessment = postTaggedEntities => {
  return _.find(postTaggedEntities, entity => {
    if (entity?.entityType === "ASSESSMENT") {
      return entity;
    }
  });
};

export const getTaggedUnitNames = postTaggedEntities => {
  const unitNames = [];
  _.forEach(postTaggedEntities, taggedEntity => {
    if (taggedEntity?.entityType === "UNIT_PLAN") {
      unitNames.push(_.get(taggedEntity, "entity.title.value", ""));
    }
  });
  return unitNames;
};

export const getTaggedUnitNameFromAssesment = postTaggedEntities => {
  return _.get(
    _.find(
      postTaggedEntities,
      taggedEntity => taggedEntity?.entityType === "ASSESSMENT"
    ),
    "entity.title.value"
  );
};

export const getPostEntity = (postTaggedEntities, entityName) => {
  return _.map(
    _.filter(postTaggedEntities, entity => entity.entityType === entityName),
    postEntity => postEntity.entity
  );
};

export const getPostEntityValues = (postTaggedEntities, entityName) => {
  return _.map(
    _.filter(postTaggedEntities, entity => entity.entityType === entityName),
    postEntity => postEntity
  );
};

export const formatTaggedEntityCreateInput = (
  postTaggedEntities,
  curriculumProgramId
) => {
  const taggedEntityCreateInput = [];
  _.forEach(postTaggedEntities, postEntity => {
    const entityInput = {};
    const entityId = _.isEqual(
      _.get(postEntity, "entityType", "CURRICULUM_PROGRAM"),
      "CURRICULUM_PROGRAM"
    )
      ? curriculumProgramId
      : _.get(postEntity, "entity.id");
    if (!_.isEmpty(entityId)) {
      entityInput.entityId = entityId;
      entityInput.entityType = _.get(
        postEntity,
        "entityType",
        "CURRICULUM_PROGRAM"
      );
      taggedEntityCreateInput.push(entityInput);
    }
  });
  return taggedEntityCreateInput;
};

export const formatTaggedEntityUpdateInput = (
  postTaggedEntities,
  oldPostTaggedEntities,
  curriculumProgramId
) => {
  const result = {
    createdTaggedEntity: [],
    // updatedTaggedEntity: [],
    removedTaggedEntity: [],
  };

  const oldPostEntityMap = _.keyBy(oldPostTaggedEntities, "id");
  const newPostEntityMap = _.keyBy(postTaggedEntities, "id");
  _.forEach(newPostEntityMap, (newObjectMap, id) => {
    const oldObjectMap = oldPostEntityMap[id];

    if (!oldObjectMap) {
      result.createdTaggedEntity.push(newObjectMap);
    }
  });

  // Check for removed elements
  _.forEach(oldPostEntityMap, (oldObjectMap, id) => {
    if (!newPostEntityMap[id]) {
      const entityId =
        _.get(oldPostEntityMap[id], "entityType") === "CURRICULUM_PROGRAM"
          ? curriculumProgramId
          : _.get(oldObjectMap, "entity.id");
      if (!_.isEmpty(entityId)) {
        result.removedTaggedEntity.push({
          entityId,
          entityType: _.get(oldObjectMap, "entityType"),
        });
      }
    }
  });
  const updatedResult = {
    createdTaggedEntity: formatTaggedEntityCreateInput(
      result.createdTaggedEntity,
      curriculumProgramId
    ),
    removedTaggedEntity: result.removedTaggedEntity,
  };
  return updatedResult;
};

export const is500MBVideoUploadLimitEnabled = () => {
  return ACLStore.can("FeatureFlag:EnableVideoUpload500MBLimit");
};

export const getIsNewGradingSetupEnabled = () => {
  return getIsConfigurationEnabledForCPAY({
    configuration: USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
  });
};

export const getFilteredStandardSetsForTagging = edges => {
  const mappedData = _.map(edges, ({ node, standardSet }) => {
    const { id, title } = node || standardSet || {};

    return {
      id,
      label: title,
    };
  });

  return mappedData;
};

export const getStandardEdges = params => {
  const { node, showCourseStandards, selectFromAllPYP, leValues } = params;
  if (showCourseStandards) {
    let edges = _.flatMap(
      _.get(node, "standardsWrapper.standardsEdge") ||
        _.get(node, "benchmarksWrapper.standardsEdge"),
      edge =>
        _.map(edge.standards || [], standard => ({
          node: standard,
          standardSetId: _.get(edge, "standardSet.id"),
        }))
    );
    if (!selectFromAllPYP && !_.isEmpty(leValues)) {
      edges = _.filter(edges, edge =>
        _.includes(leValues, _.get(edge, "node.id"))
      );
    }
    const groupedByStandardSetId = _.groupBy(edges, "standardSetId");
    return groupedByStandardSetId;
  } else {
    const edges =
      _.get(node, "standardSetWrapper.publishedStandards.edges") ||
      _.get(node, "standardSetWrapper.publishedBenchmarks.edges") ||
      [];
    return edges;
  }
};

export const getStandardsEdges = params => {
  const { node, showCourseStandards } = params;

  if (showCourseStandards) {
    const edges = _.flatMap(
      _.get(node, "standardsWrapper.standardsEdge") ||
        _.get(node, "benchmarksWrapper.standardsEdge"),
      edge => _.map(edge.standards || [], standard => ({ node: standard }))
    );

    return edges;
  } else {
    const edges =
      _.get(node, "standardSetWrapper.publishedStandards.edges") ||
      _.get(node, "standardSetWrapper.publishedBenchmarks.edges") ||
      [];
    return edges;
  }
};

export const DEFAULT_STANDARD_FILTERS = {
  grades: [],
  subjects: [],
  tags: [],
  archivalState: "ACTIVE",
};

export const getPublishedSubjectStandardQueryVariables = params => {
  const {
    id,
    searchText = "",
    standardFilters = DEFAULT_STANDARD_FILTERS,
    standardSetIds,
    curriculumProgramType,
  } = params;

  return {
    id,
    filters: {
      searchText,
      archivalState: "ACTIVE",
      standardSetIds,
      gradingTypes: ["INDIVIDUAL"],
      ...standardFilters,
    },
    isPYPCurriculum: curriculumProgramType
      ? curriculumProgramType === CURRICULUM_TYPE_PYP
      : undefined,
  };
};

export const getLocalizedFilterLabels = t => ({
  grades: t("scopeAndSequence:grade_levels"),
  subjects: t("scopeAndSequence:subject_areas"),
  tags: t("common:tags"),
  archivalState: t("common:status"),
});

export const ARCHIVAL_STATE_OPTIONS = [
  {
    id: "ALL",
    label: "common:all",
  },
  {
    id: "ACTIVE",
    label: "common:active",
  },
  {
    id: "ARCHIVED",
    label: "common:archived",
  },
];

export const FILTERS = {
  [COURSE_BASED_GRADING]: ["grades", "subjects", "tags", "archivalState"],
  [SUBJECT_BASED_GRADING]: ["grades", "tags", "archivalState"],
  null: ["grades", "tags", "archivalState"],
};

export const getFilterOverlayOptions = params => {
  const { t, grades, subjects, tags } = params;

  const localizedFilterLabels = getLocalizedFilterLabels(t);

  const academicSetupType = getAcademicSetupType();
  const filters = FILTERS[academicSetupType];

  return _.reduce(
    filters,
    (acc, filterKey) => {
      switch (filterKey) {
        case "grades": {
          if (!_.isEmpty(grades)) {
            acc.push({
              id: filterKey,
              label: localizedFilterLabels[filterKey],
              optionsType: "checkbox",
              filterOptions: _.map(grades, ({ id, name }) => ({
                id,
                label: name,
              })),
              includeAllOption: true,
              showSearch: true,
            });
          }

          return acc;
        }

        case "subjects": {
          if (!_.isEmpty(subjects)) {
            acc.push({
              id: filterKey,
              label: localizedFilterLabels[filterKey],
              optionsType: "checkbox",
              filterOptions: _.map(subjects, ({ id, name }) => ({
                id,
                label: name,
              })),
              includeAllOption: true,
              showSearch: true,
            });
          }

          return acc;
        }
        case "tags": {
          if (!_.isEmpty(tags)) {
            acc.push({
              id: filterKey,
              label: localizedFilterLabels[filterKey],
              optionsType: "checkbox",
              filterOptions: tags,
              includeAllOption: true,
              showSearch: true,
            });
          }

          return acc;
        }
        case "archivalState": {
          acc.push({
            id: filterKey,
            label: localizedFilterLabels[filterKey],
            optionsType: "radio",
            filterOptions: _.map(ARCHIVAL_STATE_OPTIONS, item => ({
              ...item,
              label: t(item.label),
            })),
          });

          return acc;
        }
        default:
          return acc;
      }
    },
    []
  );
};

export const getAllFiltersCount = ({ currentFilters }) => {
  return _.reduce(
    currentFilters,
    (acc, filterValue, filterKey) => {
      if (_.isEqual(filterValue, DEFAULT_STANDARD_FILTERS[filterKey])) {
        return acc;
      } else {
        return acc + 1;
      }
    },
    0
  );
};

export const getAppliedFiltersCountForFilter = ({ filterKey, filterValue }) => {
  const defaultFilterValue = DEFAULT_STANDARD_FILTERS[filterKey];

  if (_.isEqual(filterValue, defaultFilterValue)) {
    return 0;
  }
  return _.isArray(filterValue) ? _.size(filterValue) : 1;
};

export const getIsStandardsRevampEnabled = () => {
  return (
    ACLStore.can("FeatureFlag:ShowNewStandardsPortfolio") ||
    _.includes(window.location.href, "/portfolio-review")
  );
};
export const SUBMISSION_MAX_AUDIO_RECORD_LIMIT = 3540000; // 59 minutes in milliseconds

export const isIncreasedUploadLimitEnabled = curriculumProgramId => {
  return getSettingValue({
    name: "Common:EnableIncreasedZipAndAudioLimit",
    currentCurriculumProgramId: curriculumProgramId,
  });
};

export const getIsSubjectStandardsFilterEnabled = () => {
  return ACLStore.can("FeatureFlag:ShowGenericTagFilterSubjectStandards");
};

export const getShowYtLinkInTheatreMode = () => {
  return ACLStore.can("FeatureFlag:ShowYtLinksInTheatreMode");
};

export const DEFAULT_HIDDEN_COLUMNS = [
  DATA_GRID_COLUMN_TYPES.ID,
  DATA_GRID_COLUMN_TYPES.TAGGED_IN_UNITS,
  DATA_GRID_COLUMN_TYPES.TAGGED_IN_ASSIGNMENTS,
  DATA_GRID_COLUMN_TYPES.TAGGED_IN_PORTFOLIO,
  DATA_GRID_COLUMN_TYPES.GRADING_TYPE,
  DATA_GRID_COLUMN_TYPES.SUBJECT_AREAS,
  DATA_GRID_COLUMN_TYPES.TAGS,
  DATA_GRID_COLUMN_TYPES.DESCRIPTION,
  DATA_GRID_COLUMN_TYPES.ARCHIVE,
  DATA_GRID_COLUMN_TYPES.LIST_PARENT,
  DATA_GRID_COLUMN_TYPES.CODE,
  DATA_GRID_COLUMN_TYPES.POWER_STANDARD,
  DATA_GRID_COLUMN_TYPES.GRADE_LEVELS,
];

export const getIsStandardsV2 = () => {
  return ACLStore.can("AdminPortal:StandardsV2");
};

export const getIsParentStandardsTaggingEnabled = () => {
  return ACLStore.can("FeatureFlag:EnableParentStandardsTagging");
};

export const getIsYoutubeLink = (url = "") => {
  return (
    _.startsWith(url, "https://www.youtube.com") ||
    _.startsWith(url, "https://youtu.be")
  );
};

export const getNormalizeYouTubeUrl = url => {
  try {
    if (!url || !_.isString(url)) return url;

    if (_.includes(url, "youtube.com") || _.includes(url, "youtu.be")) {
      // Extract video ID using regex patterns
      const videoId = getYoutubeVideoId(url);

      // Return normalized URL if we successfully extracted video ID
      if (!videoId) return url;
      return `https://www.youtube.com/embed/${videoId}`;
    }

    // Return original URL if we couldn't process it
    return url;
  } catch (error) {
    console.error("Error normalizing YouTube URL:", error);
    return url; // Return the original URL in case of any error
  }
};

export const showAcademicYearFilterProfilePortfolio = () => {
  return ACLStore.can("FeatureFlag:showAcademicYearFilterProfilePortfolio");
};

export const getIsEnableFamilyConsolidatedPortfolio = () => {
  return ACLStore.can("FeatureFlag:EnableFamilyConsolidatedPortfolio");
};

export const getUserRemovedCourses = () =>
  ACLStore.can("FeatureFlag:FetchRemovedCoursesStudentProfile");

export const isNewNotesUi = () => {
  return (
    ACLStore.can("FeatureFlag:showNewNotesUI") ||
    _.includes(window.location.href, "/portfolio-review")
  );
};

export const getIsSpecifiedConceptsTaggingEnabled = () => {
  return ACLStore.can("FeatureFlag:EnableSpecifiedConceptTagging");
};

export const getIsBulkAssignmentPostCreationEnhancementEnabled = () => {
  return ACLStore.can("FeatureFlag:BulkAssignmentPostCreationOptimization");
};

export const getShouldShowCourseDisplayNameInStudentProfile = () => {
  return (
    getIsLearningCoursesEnabled() && ACLStore.can(SHOW_COURSE_DISPLAY_NAME)
  );
};

export const getShouldShowFolderCount = () => {
  return ACLStore.can("FeatureFlag:showFolderPostCount");
};

export const getIsSchedulePostTabEnabled = () => {
  return ACLStore.can("FeatureFlag:EnableScheduledTabForPosts");
};

export const getShowNewStandardsUIInV1 = () => {
  return (
    ACLStore.can("FeatureFlag:ShowNewStandardsPortfolioInV1") ||
    _.includes(window.location.href, "/portfolio-review")
  );
};

export const showStatusFilterInStudentPortfolio = () => {
  return ACLStore.can("FeatureFlag:ShowStatusFilterInStudentPortfolio");
};

export const STUDENT_COURSE_STATUS_FILTERS = {
  ALL: "ALL",
  ACTIVE: "ACTIVE",
  ARCHIVED: "ARCHIVED",
};

export const getCurrentPlannerElementSetFromState = elementType => state => {
  const currentPlannerElementSets = _.get(
    state,
    "platform.currentPlannerElementSets",
    {}
  );

  const plannerElementSet = currentPlannerElementSets[elementType] || {};

  return plannerElementSet;
};

export const isPlatformPostLinkRoute = () => {
  return _.includes(window.location.href, "platform/links/post");
};
